import {
  PLATFORM_NOTIFICATION_HEALTH_TYPE,
  type PlatformNotificationHealthType,
} from '@terra/shared-contracts-notification-center';

export type HealthIssueDisplayValues = {
  username?: string;
  role?: string;
  domain?: string;
  connectorName?: string;
  gitPlatformName?: string;
  repositoryCount?: number;
  repositoryNames?: string;
  knowledgeSourceProviderName?: string;
  unmatchedCount?: number;
};

const HEALTH_ISSUE_TRANSLATION_KEY: Record<
  PlatformNotificationHealthType,
  string
> = {
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.CREDENTIAL_VERIFICATION_FAILED]:
    'credentialVerificationFailed',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.WAF_BLOCKING_APPLICATION_URL]:
    'wafBlockingApplicationUrl',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.WAF_BLOCKING_ADDITIONAL_DOMAIN]:
    'wafBlockingAdditionalDomain',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.INTERNAL_CONNECTOR_OFFLINE]:
    'internalConnectorOffline',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.INTERNAL_CONNECTOR_REQUIRED]:
    'internalConnectorRequired',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_INTEGRATION_UNREACHABLE]:
    'sourceCodeIntegrationUnreachable',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_REPOSITORIES_UNREACHABLE]:
    'sourceCodeRepositoriesUnreachable',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_CONNECTOR_OFFLINE]:
    'sourceCodeConnectorOffline',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.MISSING_API_SPECIFICATION]:
    'missingApiSpecification',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.NO_SIGNAL_CATEGORIES_IN_SCOPE]:
    'noSignalCategoriesInScope',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.KNOWLEDGE_SOURCE_SYNC_FAILED]:
    'knowledgeSourceSyncFailed',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.CLOUD_IP_UNASSOCIATED]:
    'cloudIpUnassociated',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.ADAPTIVE_SCANNING_PAUSED]:
    'adaptiveScanningPaused',
};

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const readStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter(
        (entry): entry is string =>
          typeof entry === 'string' && entry.length > 0,
      )
    : [];

const formatGitPlatformName = ({
  connectorType,
}: {
  connectorType?: string;
}): string | undefined => {
  if (connectorType === 'github') {
    return 'GitHub';
  }

  if (connectorType === 'gitlab') {
    return 'GitLab';
  }

  return connectorType ? connectorType : undefined;
};

export const getHealthIssueTranslationKey = ({
  type,
}: {
  type: PlatformNotificationHealthType;
}): string => HEALTH_ISSUE_TRANSLATION_KEY[type];

export const extractHealthIssueDisplayValues = ({
  type,
  context,
}: {
  type: PlatformNotificationHealthType;
  context?: Record<string, unknown> | null;
}): HealthIssueDisplayValues => {
  const safeContext = context ?? {};

  switch (type) {
    case PLATFORM_NOTIFICATION_HEALTH_TYPE.CREDENTIAL_VERIFICATION_FAILED: {
      return {
        username: readString(safeContext.username),
        role: readString(safeContext.role),
      };
    }

    case PLATFORM_NOTIFICATION_HEALTH_TYPE.WAF_BLOCKING_ADDITIONAL_DOMAIN:
      return {
        domain: readString(safeContext.domain),
      };
    case PLATFORM_NOTIFICATION_HEALTH_TYPE.INTERNAL_CONNECTOR_OFFLINE:
    case PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_CONNECTOR_OFFLINE:
      return {
        connectorName: readString(safeContext.connectorName),
        gitPlatformName: formatGitPlatformName({
          connectorType: readString(safeContext.connectorType),
        }),
      };
    case PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_INTEGRATION_UNREACHABLE:
      return {
        gitPlatformName:
          formatGitPlatformName({
            connectorType: readString(safeContext.connectorType),
          }) ?? readString(safeContext.gitPlatformName),
      };
    case PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_REPOSITORIES_UNREACHABLE: {
      const repositoryNames = readStringArray(safeContext.repositoryNames);

      return {
        gitPlatformName:
          formatGitPlatformName({
            connectorType: readString(safeContext.connectorType),
          }) ?? readString(safeContext.gitPlatformName),
        repositoryCount:
          typeof safeContext.repositoryCount === 'number'
            ? safeContext.repositoryCount
            : repositoryNames.length,
        repositoryNames: repositoryNames.join(', '),
      };
    }

    case PLATFORM_NOTIFICATION_HEALTH_TYPE.KNOWLEDGE_SOURCE_SYNC_FAILED:
      return {
        knowledgeSourceProviderName:
          readString(safeContext.provider) ??
          readString(safeContext.knowledgeSourceProviderName),
      };
    case PLATFORM_NOTIFICATION_HEALTH_TYPE.CLOUD_IP_UNASSOCIATED:
      return {
        unmatchedCount:
          typeof safeContext.unmatchedCount === 'number'
            ? safeContext.unmatchedCount
            : undefined,
      };
    default:
      return {};
  }
};
